import React from "react";
import ServicePageTemplate, { BenefitItem, ProcessItem, WhyChooseItem } from "../Components/service-template/service-template";
import aiIntegration from "../Assets/image/services/ai-integration-services.webp";
import { Network, Database, Lock, Gauge, BarChart3, GitPullRequest, Map, Layout, Settings, Rocket } from "lucide-react";

function AiIntegrationServices() {
  const benefitsList: BenefitItem[] = [
    {
      title: "API-First Integration",
      description: "Connect standard LLM providers, vector engines, and custom models to your existing tech stacks with robust middleware.",
    },
    {
      title: "Unstructured Mining",
      description: "Unleash cognitive AI models to catalog, classify, and extract patterns from support logs, PDFs, and customer feedback.",
    },
    {
      title: "Seamless Workflows",
      description: "Infuse smart decision points directly into database transactions, ERP platforms, and CRM pipelines without friction.",
    },
    {
      title: "Hybrid DB Embeddings",
      description: "Storing high-dimensional dense vector embeddings directly alongside traditional relational database rows (PGVector, MySQL).",
    },
    {
      title: "Low-Latency Tiers",
      description: "Utilizing fast Redis caching and context compression to minimize model response times and limit external API costs.",
    },
    {
      title: "Telemetry Dashboards",
      description: "Comprehensive administration panels tracking API key limits, query quotas, user logs, and model response times.",
    },
  ];

  const processList: ProcessItem[] = [
    {
      step: 1,
      title: "System & API Auditing",
      icon: <Map />,
      description: "We audit your databases, current API latency limits, and schema structures for compatibility.",
    },
    {
      step: 2,
      title: "Integration Middleware",
      icon: <Layout />,
      description: "Writing lightweight Express, Rails, or Python middleware to broker data exchanges securely.",
    },
    {
      step: 3,
      title: "Pipeline Construction",
      icon: <Settings />,
      description: "Connecting databases, models, caching layers, and frontends into unified, fast-flowing data streams.",
    },
    {
      step: 4,
      title: "Production Monitoring",
      icon: <Rocket />,
      description: "Hooking up telemetry to log tokens, guardrail exceptions, and pipeline speeds.",
    },
  ];

  const whyChooseList: WhyChooseItem[] = [
    {
      id: 1,
      title: "Universal API Connectors",
      description: "Pre-engineered connectors for PostgreSQL, MySQL, Salesforce, HubSpot, and classic legacy systems.",
      icon: <Network size={24} />,
    },
    {
      id: 2,
      title: "PGVector & SQL Knowledge",
      description: "Deep expertise in storing and querying dense vectors directly alongside your existing relational database rows.",
      icon: <Database size={24} />,
    },
    {
      id: 3,
      title: "Secure Enterprise Brokers",
      description: "We enforce strict encryption, VPC setups, and API keys containment to maintain absolute system integrity.",
      icon: <Lock size={24} />,
    },
    {
      id: 4,
      title: "Optimized Performance",
      description: "Adding Redis caching tiers and prompt compression to shave precious milliseconds off user wait times.",
      icon: <Gauge size={24} />,
    },
    {
      id: 5,
      title: "Business Intelligence Insights",
      description: "Convert textual chat histories into visual dashboards tracking KPIs, sentiment, and user satisfaction.",
      icon: <BarChart3 size={24} />,
    },
    {
      id: 6,
      title: "Clean CI/CD Deployments",
      description: "Automated delivery pipelines that integrate model releases seamlessly alongside standard application deployments.",
      icon: <GitPullRequest size={24} />,
    },
  ];

  return (
    <ServicePageTemplate
      metaTitle="Enterprise AI Integration Services | Softcoding Solutions"
      metaDescription="Infuse AI capabilities into your current databases, ERP, CRM, and cloud services. Secure API integrations, middleware, and optimized caching."
      canonicalUrl="https://softcodingsolutions.com/ai-integration-services-company"
      heroTitle="AI Integration Services"
      heroSubtitle="Upgrade your current applications by infusing intelligent, real-time AI endpoints directly into your database and server layers."
      heroImage={aiIntegration}
      benefitsTitle="benefits of AI integration"
      benefitsHeading="Connect advanced artificial intelligence to your legacy tools and unlock hidden data insights."
      benefitsList={benefitsList}
      processTitle="our integration process"
      processDescription="From API auditing to optimized cloud deployments—we make legacy architectures smart with zero downtime."
      processList={processList}
      processImage={aiIntegration}
      whyChooseTitle="Why Softcoding Solutions for AI Integration"
      whyChooseList={whyChooseList}
      contactHeading="Let's make your current apps smarter."
      contactDescription="Get in touch to review your current tech stack, and let's configure a custom AI integration plan tailored for your database infrastructure."
      contactPurpose="AI Integration Services"
      contactFormTitle="Hire AI Integration Developer"
    />
  );
}

export default AiIntegrationServices;
